import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { classCheckInsService } from 'app/javascript/services/classCheckIns';
import { BulkFacialListItem } from 'app/javascript/types';

export const checkInStatsKey = (slug: string) => ['class-check-in-stats', slug];

export function useMyCheckInStats(companySlug: string, enabled = true) {
  return useQuery({
    queryKey: checkInStatsKey(companySlug),
    queryFn: () => classCheckInsService.myStats(companySlug),
    enabled,
  });
}

export const bulkFacialKey = (slug: string) => ['bulk-facial', slug];
export const bulkFacialListKey = (slug: string) => ['bulk-facial-list', slug];

export function useBulkFacialUpload(companySlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ photo }: { photo: File }) =>
      classCheckInsService.bulkFacial(companySlug, photo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bulkFacialListKey(companySlug) });
    },
  });
}

export function useBulkFacialStatus(companySlug: string, resultId: number | null) {
  return useQuery({
    queryKey: [...bulkFacialKey(companySlug), resultId],
    queryFn: () => classCheckInsService.bulkFacialStatus(companySlug, resultId!),
    enabled: resultId !== null,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.status !== 'processing') return false;
      return 2000;
    },
    refetchIntervalInBackground: false,
  });
}

export function useBulkFacialList(companySlug: string, page = 1) {
  return useQuery({
    queryKey: [...bulkFacialListKey(companySlug), page],
    queryFn: () => classCheckInsService.bulkFacialList(companySlug, page),
    staleTime: 2 * 60 * 1000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasProcessing = data.results.some(
        (r: BulkFacialListItem) => r.status === 'processing'
      );
      return hasProcessing ? 5000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useBulkFacialDestroy(companySlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resultId: number) =>
      classCheckInsService.bulkFacialDestroy(companySlug, resultId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bulkFacialListKey(companySlug) });
    },
  });
}

export function useBulkFacialReprocess(companySlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resultId: number) =>
      classCheckInsService.bulkFacialReprocess(companySlug, resultId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bulkFacialListKey(companySlug) });
      queryClient.invalidateQueries({ queryKey: bulkFacialKey(companySlug) });
    },
  });
}

export function useAssignBulkFacialSchedule(companySlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ resultId, class_schedule_id, class_date, class_time }: { resultId: number; class_schedule_id: number | null; class_date: string; class_time: string | null }) =>
      classCheckInsService.bulkFacialAssignSchedule(companySlug, resultId, { class_schedule_id, class_date, class_time }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bulkFacialListKey(companySlug) });
    },
  });
}

export function useCreateManualCheckIn(companySlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ resultId, userId }: { resultId: number; userId: number }) =>
      classCheckInsService.createManualCheckIn(companySlug, resultId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bulkFacialKey(companySlug) });
      queryClient.invalidateQueries({ queryKey: bulkFacialListKey(companySlug) });
      queryClient.invalidateQueries({ queryKey: checkInStatsKey(companySlug) });
    },
  });
}

export const scheduleStatsKey = (slug: string) => ['schedule-stats', slug];

export function useScheduleStats(companySlug: string, enabled = true) {
  return useQuery({
    queryKey: scheduleStatsKey(companySlug),
    queryFn: () => classCheckInsService.scheduleStats(companySlug),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}


export function useDeleteCheckIn(companySlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (checkInId: number) =>
      classCheckInsService.deleteCheckIn(companySlug, checkInId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bulkFacialListKey(companySlug) });
      queryClient.invalidateQueries({ queryKey: bulkFacialKey(companySlug) });
      queryClient.invalidateQueries({ queryKey: checkInStatsKey(companySlug) });
    },
  });
}
